import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {AppOauth2RefreshTokenRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/AppOauth2RefreshTokenRequest';
import {AppOauth2RevokeTokenRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/AppOauth2RevokeTokenRequest';
import {AppOauth2AccessTokenResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/AppOauth2AccessTokenResponse';
import {AppOAuth2CredentialsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/AppOAuth2CredentialsResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {OAuth2ProviderName} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/OAuth2';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import bind from 'bind-decorator';

export class OAuth2Controller
{
    @bind
    private _currentContext():{organizationSid:string}
    {
        const {context} = commonControllers;

        const organizationSid = context.currentOrganizationSid();

        return {organizationSid};
    }

    @bind
    public async getOAuth2Credentials(provider:OAuth2ProviderName, appName:string):Promise<AppOAuth2CredentialsResponse>
    {
        const {organizationSid} = this._currentContext();

        return await API.appOauth2Credentials.getAppCredentials(organizationSid, provider, appName);
    }

    @bind
    public async clientCredentials(provider:OAuth2ProviderName, appName:string):Promise<AppOauth2AccessTokenResponse>
    {
        const {organizationSid} = this._currentContext();

        return await API.appOauth2.clientCredentials(provider, organizationSid, appName);
    }

    @bind
    public async refreshToken(provider:OAuth2ProviderName, appName:string, refreshToken:string):Promise<AppOauth2AccessTokenResponse>
    {
        const {organizationSid} = this._currentContext();
        return await API.appOauth2.refreshToken(provider, organizationSid, appName, new AppOauth2RefreshTokenRequest(refreshToken));
    }

    @bind
    public async getTokenInfo(appOAuth2CredentialsName:string,
                              provider:OAuth2ProviderName,
                              accessToken:string):Promise<any | AError>
    {
        const credentials:AppOAuth2CredentialsResponse = await commonControllers.appOAuth2.getOAuth2Credentials(
            provider,
            appOAuth2CredentialsName
        );

        const authProviderTokenInfoUrl = this._getAuthProviderTokenInfoUrl(provider, credentials);

        if( !authProviderTokenInfoUrl )
        {
            return new AError('Wrong authProviderName', `Auth provider ${provider} is not supported`);
        }

        const authProviderTokenInfoUrlWithQueryParams = `${authProviderTokenInfoUrl}?access_token=${accessToken}`;

        const response = await fetch(
            authProviderTokenInfoUrlWithQueryParams,
            {
                method: 'POST',
                redirect: 'manual'
            }
        );

        return response.ok ? await response.json() : null;
    }

    @bind
    public async revokeToken(appOAuth2CredentialsName:string,
                             provider:OAuth2ProviderName,
                             organizationSid:string,
                             accessToken:string,
                             refreshToken:string):Promise<any>
    {
        if( provider === OAuth2ProviderName.OKTA || provider === OAuth2ProviderName.MICROSOFT )
        {
            return API.appOauth2.revokeToken(
                provider,
                organizationSid,
                appOAuth2CredentialsName,
                new AppOauth2RevokeTokenRequest(refreshToken || accessToken, !!refreshToken)
            );
        }
        else
        {
            const authProviderRevokeTokenUrl = this._getAuthProviderRevokeTokenUrl(provider);
            if( !authProviderRevokeTokenUrl )
            {
                return null;
                // return new AError('Wrong authProviderName', `Auth provider ${provider} is not supported`);
            }

            const authProviderTokenInfoUrlWithQueryParams = `${authProviderRevokeTokenUrl}?token=${accessToken}`;

            const response = await fetch(authProviderTokenInfoUrlWithQueryParams,
                {
                    method: 'POST',
                    redirect: 'manual'
                });

            return response.ok ? await response.json() : null;
        }
    }

    public getAuthProviderConsentScreenUrl(authProviderName:OAuth2ProviderName,
                                           credentials:AppOAuth2CredentialsResponse)
    {
        let authProviderUrl:string;

        switch( authProviderName )
        {
            case OAuth2ProviderName.GOOGLE:
                authProviderUrl = 'https://accounts.google.com/o/oauth2/v2/auth';
                break;
            case OAuth2ProviderName.FACEBOOK:
                authProviderUrl = 'https://www.facebook.com/v5.0/dialog/oauth';
                break;
            case OAuth2ProviderName.MICROSOFT:
                authProviderUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
                break;
            case OAuth2ProviderName.FINASTRA:
                authProviderUrl = 'https://api.fusionfabric.cloud/login/v1/sandbox/oidc/authorize';
                break;
            case OAuth2ProviderName.OKTA:
                authProviderUrl = 'https://${custom_domain}/oauth2/v1/authorize';
                break;
            default:
                authProviderUrl = null;
        }

        if( credentials.customDomain )
            authProviderUrl = authProviderUrl.replace('${custom_domain}', credentials.customDomain);

        return authProviderUrl;
    }

    private _getAuthProviderTokenInfoUrl(authProviderName:OAuth2ProviderName,
                                         credentials:AppOAuth2CredentialsResponse)
    {
        let authProviderUrl:string;

        switch( authProviderName )
        {
            case OAuth2ProviderName.GOOGLE:
                authProviderUrl = 'https://www.googleapis.com/oauth2/v1/tokeninfo';
                break;
            case OAuth2ProviderName.FACEBOOK:
                authProviderUrl = 'https://graph.facebook.com/debug_token';
                break;
            case OAuth2ProviderName.MICROSOFT:
                authProviderUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/token';
                break;
            case OAuth2ProviderName.FINASTRA:
                authProviderUrl = 'https://api.fusionfabric.cloud/login/v1/sandbox/oidc/token';
                break;
            case OAuth2ProviderName.OKTA:
                authProviderUrl = 'https://${custom_domain}/oauth2/v1/token';
                break;
            default:
                authProviderUrl = null;
        }

        if( credentials.customDomain )
            authProviderUrl = authProviderUrl.replace('${custom_domain}', credentials.customDomain);

        return authProviderUrl;
    }

    private _getAuthProviderRevokeTokenUrl(authProviderName:OAuth2ProviderName)
    {
        switch( authProviderName )
        {
            case OAuth2ProviderName.GOOGLE:
                return 'https://accounts.google.com/o/oauth2/revoke';
            case OAuth2ProviderName.MICROSOFT:
                return 'https://login.microsoftonline.com/common/oauth2/logout';
            case OAuth2ProviderName.OKTA:
                return 'https://${custom_domain}/oauth2/v1/revoke';
            default:
                return null;
        }
    }
}
